import { useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import c from 'classnames';
import {
  ButtonColors,
  ButtonVariants,
  Card,
  DesignButton,
  Icon,
  OrderKitButton,
  Typography,
  ActionsCard,
} from 'components';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import { ActionPlanItem } from 'types/ActionPlan';

import { BabyGutInfo } from '../GutType/BabyGutInfo';
import { GutCstCard } from './MicrobiomeCards/GutCst/GutCstCard';
import MicrobiomeBreakdownCard from './MicrobiomeCards/MicrobiomeBreakdown/MicrobiomeBreakdownCard';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';

import styles from './Microbiome.module.css';

export const MicrobiomeRoutes = () => {
  return (
    <Routes>
      <Route path={``} key='microbiome' element={<Microbiome />} />
    </Routes>
  );
};

const filterOutcomesBySection = (data: ActionPlanItem, section = '') => {
  const cardData = {
    ...data,
    display_title: data.display_title,
    detail: data.detail,
    desired_outcomes: section
      ? data.desired_outcomes?.filter(outcome => outcome.section === section)
      : data.desired_outcomes,
    id: data.id,
  };
  return cardData;
};

export const Microbiome = () => {
  const { currentKitId, currentKitIsBabySample } =
    useCurrentKitAndTinyAccount();
  const { data: actionPlanFromDBResults } = useActionPlanData({
    kitId: currentKitId,
  });

  const actionsRef = useRef<HTMLDivElement>(null);

  const actionPlanItemsFiltered = !actionPlanFromDBResults?.length
    ? []
    : actionPlanFromDBResults
        .filter(
          (item: ActionPlanItem) =>
            !!item.desired_outcomes?.length &&
            item.desired_outcomes
              .map(outcome => outcome.section)
              .includes('Baby Gut Type'),
        )
        .map((item: ActionPlanItem) =>
          filterOutcomesBySection(item, 'Baby Gut Type'),
        );

  if (!currentKitId) {
    return (
      <div>
        <Typography variant='body-s'>
          You have not taken any gut samples yet or your results are not yet
          ready.
        </Typography>
        <OrderKitButton>Order a Tiny&nbsp;Gut&nbsp;Kit</OrderKitButton>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Breadcrumb className={styles.breadcrumb} />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <div className={styles.wideSection}>
            {actionPlanItemsFiltered.length > 0 && (
              <Card className={c(styles.actionsReminder, 'hide-on-desktop')}>
                <div
                  className='flex gap-2'
                  onClick={() => {
                    if (actionsRef.current) {
                      window.scrollTo({
                        top: actionsRef.current.offsetTop - 10,
                        behavior: 'smooth',
                      });
                    }
                  }}
                >
                  <div className={styles.iconWrapper}>
                    <Icon name='flashFilled' size='m' />
                  </div>
                  <div className='flex flex-column gap-1'>
                    <Typography variant='heading-s'>{`${actionPlanItemsFiltered.length} actions on your action plan`}</Typography>
                    <DesignButton
                      label='Jump to actions'
                      iconNameRight='chevronForward'
                      color={ButtonColors.WHITE}
                      variant={ButtonVariants.TEXT}
                    />
                  </div>
                </div>
              </Card>
            )}
            <GutCstCard />
            {currentKitIsBabySample && (
              <Card title='Baby Gut Types'>
                <BabyGutInfo />
              </Card>
            )}
            <MicrobiomeBreakdownCard />
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          {/* Keep this wrapper mounted even when there are no actions and keep
              the guard inside it (ENG-4509). An empty wrapper keeps the
              TwoColumnGrid.Slim column rendered so the main column stays the
              same width; removing it would collapse the column and widen the
              main content. */}
          <div ref={actionsRef}>
            {actionPlanItemsFiltered.length > 0 && (
              <ActionsCard
                title='Action Items'
                actions={actionPlanItemsFiltered}
              />
            )}
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </div>
  );
};
